import { useFilePicker } from '@/components/FilePicker/FilePickerProvider';
import { Container } from '@/components/Layout/Container';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/constants';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LangSelector } from './LangSelector';

const SUPPORT_EMAIL = 'support@editar-pdf.com';

/** §7 footer: ink, 64px top padding, a closing CTA, mono-eyebrow link columns, mono legal row. */
function Footer() {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.layout.footer' });
  const { t: tHeader } = useTranslation('web', { keyPrefix: 'components.layout.header' });
  const { open: openFilePicker } = useFilePicker();

  const lang = i18n.language;

  const columns = [
    {
      title: t('columns.tools'),
      links: [
        { label: tHeader('editPdf'), to: ROUTES.home(lang) },
        { label: tHeader('pdfToWord'), to: ROUTES.convertToWord(lang) },
      ],
    },
    {
      title: t('columns.company'),
      links: [
        { label: tHeader('pricing'), to: ROUTES.pricing(lang) },
        { label: t('support'), href: `mailto:${SUPPORT_EMAIL}` },
      ],
    },
    {
      title: t('columns.legal'),
      links: [
        { label: t('terms'), to: ROUTES.terms(lang) },
        { label: t('privacyPolicy'), to: ROUTES.policy(lang) },
        { label: t('refundPolicy'), to: ROUTES.refund(lang) },
        { label: t('gdpr'), to: ROUTES.gdpr(lang) },
      ],
    },
  ];

  return (
    <footer className="bg-ink pt-12 pb-7 md:pt-16">
      <Container className="flex flex-col gap-12">
        <div className="flex flex-col gap-10 md:flex-row md:gap-16">
          <div className="flex flex-1 flex-col gap-5">
            <h2 className="max-w-[320px] text-[40px] leading-[1.04] font-extrabold tracking-[-0.045em] text-surface-1">
              {t('cta.title')}
            </h2>
            <div className="flex flex-wrap gap-2.5">
              <Button variant="accent" onClick={openFilePicker}>
                {t('cta.chooseFile')}
              </Button>
              <Button variant="ink" className="border border-ink-border" asChild>
                <Link to={ROUTES.pricing(lang)}>{t('cta.seePricing')}</Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 md:gap-16">
            {columns.map((column) => (
              <div key={column.title} className="flex flex-col gap-3">
                <span className="text-mono-eyebrow">{column.title}</span>
                {column.links.map((link) =>
                  link.to ? (
                    <Link
                      key={link.label}
                      to={link.to}
                      className="text-[14.5px] text-ink-muted transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) hover:text-surface-1"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      className="text-[14.5px] text-ink-muted transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) hover:text-surface-1"
                    >
                      {link.label}
                    </a>
                  ),
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-ink-raised pt-6">
          <span className="font-mono text-[12px] text-muted-foreground">
            © {new Date().getFullYear()} editar{'‑'}pdf
          </span>
          <span className="font-mono text-[12px] text-muted-foreground">{t('rights')}</span>
          <LangSelector />
        </div>
      </Container>
    </footer>
  );
}

export default Footer;
